import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Users, LogOut, ShieldCheck } from "lucide-react";

const L = {
  en: { title: "My Team", subtitle: "Join your club's team with the code your staff gave you. Control exactly what they can see.",
    join: "Join a team", codePh: "TWN-XXXX-XXXX", joinBtn: "Join", myTeams: "My teams", none: "You haven't joined a team yet.", leave: "Leave",
    privacy: "What my club can see", privacyHint: "Readiness, injury risk and pain alerts are always shared with your staff for your safety.",
    share_daily: "Daily log (mood, energy, sleep…)", share_medical: "Injuries & timeline", share_stats: "Physical stats", share_reports: "Weekly AI reports", share_videos: "Video analyses", joined: "Joined" },
  fr: { title: "Mon Équipe", subtitle: "Rejoins l'équipe de ton club avec le code donné par ton staff. Tu contrôles exactement ce qu'il voit.",
    join: "Rejoindre une équipe", codePh: "TWN-XXXX-XXXX", joinBtn: "Rejoindre", myTeams: "Mes équipes", none: "Tu n'as pas encore rejoint d'équipe.", leave: "Quitter",
    privacy: "Ce que mon club peut voir", privacyHint: "Fraîcheur, risque blessure et alertes douleur sont toujours partagés avec ton staff pour ta sécurité.",
    share_daily: "Suivi quotidien (humeur, énergie, sommeil…)", share_medical: "Blessures & timeline", share_stats: "Stats physiques", share_reports: "Rapports IA hebdo", share_videos: "Analyses vidéo", joined: "Rejoint le" },
};

const KEYS = ["share_daily", "share_medical", "share_stats", "share_reports", "share_videos"];

export default function Team() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const { user, setUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const privacy = { share_daily: true, share_medical: true, share_stats: true, share_reports: true, share_videos: true, ...(user?.privacy || {}) };

  const load = () => api.get("/player/teams").then((r) => setTeams(r.data));
  useEffect(() => { load(); }, []);

  const join = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/player/teams/join", { code: code.trim().toUpperCase() });
      toast.success(`${r.data.club?.club_name || ""} · ${r.data.name}`);
      setCode(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const leave = async (id) => { await api.delete(`/player/teams/${id}`); load(); };

  const toggle = async (k, v) => {
    const r = await api.put("/player/privacy", { [k]: v });
    setUser({ ...user, privacy: r.data });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="team-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">CLUB · SQUAD</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="team-title">{t.title}</h1>
      <p className="text-slate-400 mt-2 max-w-2xl">{t.subtitle}</p>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
            <div className="font-display font-bold uppercase text-lg mb-3">{t.join}</div>
            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t.codePh} data-testid="team-code-input" className="bg-[#090C10] border-[#1E2638] text-white font-mono-tech tracking-widest uppercase" />
              <Button onClick={join} disabled={busy || !code.trim()} data-testid="team-join-btn" className="bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase">{t.joinBtn}</Button>
            </div>
          </div>

          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
            <div className="font-display font-bold uppercase text-lg mb-3">{t.myTeams}</div>
            {teams.length === 0 && <div className="text-slate-500 text-sm" data-testid="team-empty">{t.none}</div>}
            <div className="space-y-2">
              {teams.map((tm) => (
                <div key={tm.id} className="rounded-lg border border-[#1E2638] bg-[#090C10] p-4 flex items-center gap-3" data-testid={`team-item-${tm.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#00F076]/10 border border-[#00F076]/30 flex items-center justify-center"><Users className="w-5 h-5 text-[#00F076]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold">{tm.club?.club_name} <span className="text-slate-500">·</span> {tm.name}</div>
                    <div className="text-[10px] font-mono-tech uppercase text-slate-400">{tm.category} {tm.club?.city && `· ${tm.club.city}`} · {t.joined} {new Date(tm.joined_at).toLocaleDateString()}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => leave(tm.id)} data-testid={`team-leave-${tm.id}`} className="text-slate-400 hover:text-red-400 text-xs"><LogOut className="w-3 h-3 mr-1" />{t.leave}</Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6" data-testid="team-privacy">
          <div className="font-display font-bold uppercase text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#00F076]" />{t.privacy}</div>
          <p className="text-xs text-slate-400 mt-1 mb-4">{t.privacyHint}</p>
          <div className="space-y-3">
            {KEYS.map((k) => (
              <div key={k} className="flex items-center justify-between rounded-lg border border-[#1E2638] bg-[#090C10] px-4 py-3">
                <Label className="text-sm text-slate-200">{t[k]}</Label>
                <Switch checked={!!privacy[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`privacy-${k}`} className="data-[state=checked]:bg-[#00F076]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
